import { AxiosResponse } from "axios";
import {
    ReimbursementHistoryResponse,
    ReimbursementListResponse,
    ReimbursementResponse,
} from "./reimbursementInterface";
import apiService from "services/apiService";

export interface StorageTypeResponse {
    storage: "DRIVE" | "S3";
}

export interface UploadUrlResponse {
    uploadUrl: string;
    key: string;
}

class ReimbursementManager {
    private url: string;
    constructor() {
        this.url = `api/reimbursement`;
    }

    async getStorageType(): Promise<AxiosResponse<StorageTypeResponse>> {
        return await apiService.get(`api/admin/storage`);
    }

    async getUploadUrl(fileName: string): Promise<AxiosResponse<UploadUrlResponse>> {
        return await apiService.post(`api/upload-url`, { fileName });
    }

    async getAllReimbursementData(): Promise<AxiosResponse<Array<ReimbursementResponse>>> {
        return await apiService.get(this.url);
    }

    async getReimbursementData(params: {
        page: number;
        size: number;
        status?: string;
        username?: string;
        fromDate?: string;
        toDate?: string;
        type?: string;
        search?: string;
    }): Promise<AxiosResponse<ReimbursementResponse[] | ReimbursementListResponse>> {
        const query = new URLSearchParams();
        query.set("page", String(params.page));
        query.set("size", String(params.size));

        if (params.status) query.set("status", params.status);
        if (params.username) query.set("username", params.username);
        if (params.fromDate) query.set("fromDate", params.fromDate);
        if (params.toDate) query.set("toDate", params.toDate);
        if (params.type) query.set("type", params.type);
        // Free-text search term from the listing search box.
        if (params.search) query.set("search", params.search);

        return await apiService.get(`${this.url}?${query.toString()}`);
    }

    async getReimbursementHistory(id: number): Promise<AxiosResponse<Array<ReimbursementHistoryResponse>>> {
        return await apiService.get(`${this.url}/history/${id}`);
    }

    async addReimbursement(formData: FormData): Promise<AxiosResponse> {
        return await apiService.post(this.url, formData)
    }

    // Update supports multiple attachments:
    //  • deleteFileIds — ids of existing files the user removed (comma-separated)
    //  • newKeys       — S3 object keys of newly uploaded files (S3 mode only)
    // DRIVE mode sends the new files as multipart `newFiles` parts inside formData.
    async updateReimbursement(
        formData: FormData,
        params?: { deleteFileIds?: number[]; newKeys?: string[] },
    ): Promise<AxiosResponse> {
        const query = new URLSearchParams();
        if (params?.deleteFileIds?.length) query.set("deleteFileIds", params.deleteFileIds.join(","));
        if (params?.newKeys?.length) params.newKeys.forEach((key) => query.append("newKeys", key));

        const queryString = query.toString();
        const requestUrl = queryString ? `${this.url}?${queryString}` : this.url;
        return await apiService.put(requestUrl, formData);
    }

    async deleteReimbursement(id: number): Promise<AxiosResponse> {
        return await apiService.delete(`${this.url}/${id}`);
    }

    // Remove a single attachment from a reimbursement:
    //   DELETE api/reimbursement/{reimbursementId}/file/{fileId}
    async deleteReimbursementFile(reimbursementId: number, fileId: number): Promise<AxiosResponse> {
        return await apiService.delete(`${this.url}/${reimbursementId}/file/${fileId}`);
    }
}

const reimbursementManager = new ReimbursementManager();
export default reimbursementManager;